// src/projects/SubscriptionPlansLayout.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Alert,
  Button,
  Divider,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Switch,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Check as CheckIcon,
  Star as StarIcon,
  WorkspacePremium as PremiumIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  PlayArrow as PlayArrowIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import useStore from '../config/store';
import api from '../config/api';

const SubscriptionPlansLayout = () => {
  const { t } = useTranslation();
  const { organization, organizationName, authToken, showAlert } = useStore();

  // Estados principales
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, type: '', data: null });
  const [termsModal, setTermsModal] = useState({ open: false, targetPlan: null, subscriptionType: 'monthly', accepted: false });
  const [upgradeModal, setUpgradeModal] = useState({ open: false, targetPlan: null, currentPlan: null });
  const [isYearlyView, setIsYearlyView] = useState(false);

  // Precios correctos del backend
  const PRICING = {
    starter: { monthly: 0, yearly: 0 },
    crece: { monthly: 450, yearly: 4860 },
    avanza: { monthly: 720, yearly: 7776 },
    escala: { monthly: null, yearly: null }
  };

  // Función para calcular características según el modo (mensual/anual)
  const getFeatures = (planId, isYearly = false) => {
    const pointsConfig = {
      starter: 1,
      crece: 30,
      avanza: 60,
      escala: -1
    };

    const usersProjects = {
      starter: { users: 1, projects: 1 },
      crece: { users: 5, projects: 3 },
      avanza: { users: 10, projects: 5 },
      escala: { users: 20, projects: 10 }
    };

    return {
      points: pointsConfig[planId] || 0,
      users: usersProjects[planId]?.users || 1,
      projects: usersProjects[planId]?.projects || 1
    };
  };

  // Función para formatear características como texto
  const formatFeature = (type, value) => {
    if (type === 'points') {
      if (value === -1) {
        return `∞ ${t('subscription.points')}`;
      }
      return `${value} ${t('subscription.points')}`;
    }

    if (type === 'users') {
      return `${value} ${value === 1 ? t('subscription.user') : t('subscription.users')}`;
    }

    if (type === 'projects') {
      return `${value} ${value === 1 ? t('subscription.project') : t('subscription.projects')}`;
    }

    return value;
  };

  // Definición de planes dinámica según el modo
  const getPlans = (isYearly = false) => {
    const basePlans = [
      {
        id: "starter",
        name: t('subscription.plans.starter.name'),
        price: 0,
        currency: "USD",
        period: t('subscription.free'),
        description: t('subscription.plans.starter.description'),
        recommended: false,
        customPricing: false
      },
      {
        id: "crece",
        name: t('subscription.plans.crece.name'),
        price: isYearly ? 4860 : 450,
        currency: "USD",
        period: isYearly ? t('subscription.yearly') : t('subscription.monthly'),
        description: t('subscription.plans.crece.description'),
        recommended: true,
        customPricing: false
      },
      {
        id: "avanza",
        name: t('subscription.plans.avanza.name'),
        price: isYearly ? 7776 : 720,
        currency: "USD",
        period: isYearly ? t('subscription.yearly') : t('subscription.monthly'),
        description: t('subscription.plans.avanza.description'),
        recommended: false,
        customPricing: false
      },
      {
        id: "escala",
        name: t('subscription.plans.escala.name'),
        price: null,
        currency: "USD",
        period: isYearly ? t('subscription.yearly') : t('subscription.monthly'),
        description: t('subscription.plans.escala.description'),
        recommended: false,
        customPricing: true
      }
    ];

    return basePlans.map(plan => {
      const features = getFeatures(plan.id, isYearly);

      return {
        ...plan,
        features: [
          formatFeature('points', features.points),
          formatFeature('users', features.users),
          formatFeature('projects', features.projects)
        ]
      };
    });
  };

  const plans = getPlans(isYearlyView);

  // Ranking de planes para validar upgrades/downgrades
  const PLAN_RANK = { starter: 1, crece: 2, avanza: 3, escala: 4 };

  // APIs
  const getActiveSubscription = async () => {
    const response = await api.get(
      `/subscriptions/organization/${organization}/active`,
      { headers: { Authorization: `Bearer ${authToken}` } }
    );
    return response.data;
  };

  const getPaymentHistory = async () => {
    const response = await api.get(
      `/payments/organization/${organization}`,
      { headers: { Authorization: `Bearer ${authToken}` } }
    );
    return response.data;
  };

  const createSubscription = async (plan, subscriptionType) => {
    const response = await api.post(
      '/payments/paypal/create-subscription',
      { organizationId: organization, plan, subscriptionType },
      { headers: { Authorization: `Bearer ${authToken}` } }
    );
    return response.data;
  };

  const cancelSubscription = async (paypalSubscriptionId) => {
    const response = await api.post(
      `/subscriptions/paypal/cancel/${paypalSubscriptionId}`,
      { reason: t('subscription.actions.suspendReason') },
      { headers: { Authorization: `Bearer ${authToken}` } }
    );
    return response.data;
  };

  const suspendSubscription = async (paypalSubscriptionId) => {
    const response = await api.post(
      `/subscriptions/paypal/suspend/${paypalSubscriptionId}`,
      { reason: t('subscription.actions.suspendReason') },
      { headers: { Authorization: `Bearer ${authToken}` } }
    );
    return response.data;
  };

  const reactivateSubscription = async (paypalSubscriptionId) => {
    const response = await api.post(
      `/subscriptions/paypal/activate/${paypalSubscriptionId}`,
      { reason: t('subscription.actions.reactivateReason') },
      { headers: { Authorization: `Bearer ${authToken}` } }
    );
    return response.data;
  };

  // Cargar datos
  const loadSubscriptionData = async () => {
    try {
      setLoading(true);
      const [subResponse, paymentResponse] = await Promise.all([
        getActiveSubscription(),
        getPaymentHistory()
      ]);

      if (subResponse.response?.status_response === 'OK') {
        setSubscriptionData(subResponse.response.data);
      } else {
        console.log('No active subscription found, using starter plan');
        setSubscriptionData(null); // Plan gratuito
      }

      if (paymentResponse.response?.status_response === 'OK') {
        setPaymentHistory(paymentResponse.response.data || []);
      }
    } catch (error) {
      console.error('Error loading subscription data:', error);
      showAlert(t('subscription.errors.loadingData'), 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (organization && authToken) {
      loadSubscriptionData();
    }
  }, [organization]);

  const handleSubscribe = (plan) => {
    // Determinar el tipo de suscripción basado en el switch
    const actualSubscriptionType = isYearlyView ? 'yearly' : 'monthly';

    // Siempre abrir primero el modal de términos y condiciones
    setTermsModal({
      open: true,
      targetPlan: plan,
      subscriptionType: actualSubscriptionType,
      accepted: false
    });
  };

  const handleTermsAccepted = async () => {
    if (!termsModal.accepted) {
      showAlert(t('subscription.terms.mustAccept'), 'warning');
      return;
    }

    // Cerrar modal de términos
    setTermsModal({ open: false, targetPlan: null, subscriptionType: 'monthly', accepted: false });

    // Proceder con la suscripción original
    const { targetPlan, subscriptionType } = termsModal;

    if (targetPlan === 'starter') {
      await processSubscription(targetPlan, subscriptionType);
    } else {
      // Para planes pagados, verificar si es upgrade
      const currentPlanRank = PLAN_RANK[getCurrentPlan()];
      const targetPlanRank = PLAN_RANK[targetPlan];

      if (targetPlanRank > currentPlanRank) {
        // Es upgrade, mostrar modal de confirmación
        setUpgradeModal({
          open: true,
          targetPlan,
          currentPlan: getCurrentPlan(),
          subscriptionType
        });
      } else {
        // Suscripción directa
        await processSubscription(targetPlan, subscriptionType);
      }
    }
  };

  const processSubscription = async (plan, subscriptionType = 'monthly') => {
    setActionLoading(true);
    try {
      const response = await createSubscription(plan, subscriptionType);
      if (response.resultCode === 201) {
        const { approvalUrl } = response.response;

        // Abrir popup de PayPal
        const popup = window.open(approvalUrl, 'paypal', 'width=800,height=600,scrollbars=yes');

        // Escuchar mensajes del popup
        const handleMessage = (event) => {
          if (event.origin !== window.location.origin) return;

          if (event.data.type === 'PAYMENT_SUCCESS') {
            showAlert(t('subscription.messages.paymentProcessed'), 'success');
            setTimeout(() => loadSubscriptionData(), 3000);
          } else if (event.data.type === 'PAYMENT_CANCELLED') {
            showAlert(t('subscription.messages.paymentCancelled'), 'warning');
          }

          setActionLoading(false);
          window.removeEventListener('message', handleMessage);
        };

        window.addEventListener('message', handleMessage);

        // Fallback: monitorear cierre del popup
        const checkClosed = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkClosed);
            // Solo si no recibimos mensaje, asumir cancelación
            setTimeout(() => {
              setActionLoading(false);
              window.removeEventListener('message', handleMessage);
              showAlert(t('subscription.messages.popupClosed'), 'info');
              setTimeout(() => loadSubscriptionData(), 3000);
            }, 1000);
          }
        }, 1000);
      }
    } catch (error) {
      console.error('Error creating subscription:', error);
      showAlert(t('subscription.errors.processSubscription'), 'error');
      setActionLoading(false);
    }
  };

  const handleUpgradeConfirmed = async () => {
    const { targetPlan, subscriptionType } = upgradeModal;

    setActionLoading(true);
    try {
      // 1. Cancelar suscripción actual
      if (subscriptionData?.paypal_subscription_id) {
        await cancelSubscription(subscriptionData.paypal_subscription_id);
      }

      // 2. Crear nueva suscripción
      await processSubscription(targetPlan, subscriptionType);

      setUpgradeModal({ open: false, targetPlan: null, currentPlan: null });
    } catch (error) {
      console.error('Error in upgrade process:', error);
      showAlert(t('subscription.errors.upgrade'), 'error');
      setActionLoading(false);
    }
  };

  const handleSuspend = async () => {
    if (!subscriptionData?.paypal_subscription_id) return;

    setActionLoading(true);
    try {
      const response = await suspendSubscription(subscriptionData.paypal_subscription_id);

      if (response.resultCode === 200) {
        showAlert(t('subscription.messages.suspended'), 'info');
        loadSubscriptionData();
      } else {
        showAlert(t('subscription.errors.suspend'), 'error');
      }
    } catch (error) {
      console.error('Error suspending subscription:', error);
      showAlert(t('subscription.errors.processSuspension'), 'error');
    } finally {
      setActionLoading(false);
      setConfirmModal({ open: false, type: '', data: null });
    }
  };

  const handleReactivate = async () => {
    if (!subscriptionData?.paypal_subscription_id) return;

    setActionLoading(true);
    try {
      const response = await reactivateSubscription(subscriptionData.paypal_subscription_id);

      if (response.resultCode === 200) {
        showAlert(t('subscription.messages.reactivated'), 'success');
        loadSubscriptionData();
      } else {
        showAlert(t('subscription.errors.reactivate'), 'error');
      }
    } catch (error) {
      console.error('Error reactivating subscription:', error);
      showAlert(t('subscription.errors.processReactivation'), 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // Funciones de utilidad
  const getCurrentPlan = () => {
    if (!subscriptionData) return 'starter';
    const plan = subscriptionData.plan || 'starter';
    // Normalizar plan (el backend puede usar 'free' o 'starter')
    return plan === 'free' ? 'starter' : plan;
  };

  const canUpgradeToplan = (targetPlan) => {
    const currentPlan = getCurrentPlan();

    // Desde plan gratuito (starter) puede ir a cualquier plan pagado
    if (currentPlan === 'starter') {
      return targetPlan !== 'starter';
    }

    // Si no tiene suscripción data, es gratuito
    if (!subscriptionData) return targetPlan !== 'starter';

    // Si tiene suscripción activa con PayPal, solo puede hacer upgrade
    if (subscriptionData.status === 'active' && subscriptionData.paypal_subscription_id) {
      return PLAN_RANK[targetPlan] > PLAN_RANK[currentPlan];
    }

    // Si está suspendida, no puede cambiar hasta que expire
    if (subscriptionData.status === 'suspended') {
      return false;
    }

    // Si hay un pago reciente completado pero la suscripción no se ha actualizado,
    // permitir solo upgrades desde el plan del pago más reciente
    const recentPayment = paymentHistory
      .filter(p => p.status === 'completed')
      .sort((a, b) => {
        const timeA = a.created_at._seconds || a.created_at.seconds || 0;
        const timeB = b.created_at._seconds || b.created_at.seconds || 0;
        return timeB - timeA;
      })[0];

    if (recentPayment && recentPayment.plan && recentPayment.amount > 0) {
      return PLAN_RANK[targetPlan] > PLAN_RANK[recentPayment.plan];
    }

    // Para otros casos (como starter activo sin PayPal), puede upgradear
    return PLAN_RANK[targetPlan] > PLAN_RANK[currentPlan];
  };

  const formatDate = (date) => {
    if (!date) return '-';

    // Manejar timestamps de Firebase
    if (date._seconds) {
      return new Date(date._seconds * 1000).toLocaleDateString('es-ES');
    }

    // Manejar otros formatos de fecha
    if (date.seconds) {
      return new Date(date.seconds * 1000).toLocaleDateString('es-ES');
    }

    // Fecha normal
    return new Date(date).toLocaleDateString('es-ES');
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      active: { label: t('subscription.status.active'), color: 'success', icon: <CheckIcon /> },
      suspended: { label: t('subscription.status.suspended'), color: 'warning', icon: <WarningIcon /> },
      expired: { label: t('subscription.status.expired'), color: 'error', icon: <CancelIcon /> },
      pending: { label: t('subscription.status.pending'), color: 'info', icon: <ScheduleIcon /> },
      completed: { label: t('subscription.status.completed'), color: 'success', icon: <CheckIcon /> },
      failed: { label: t('subscription.status.failed'), color: 'error', icon: <CancelIcon /> },
      cancelled: { label: t('subscription.status.cancelled'), color: 'warning', icon: <CancelIcon /> }
    };

    const config = statusConfig[status] || statusConfig.pending;

    return (
      <Chip
        label={config.label}
        color={config.color}
        icon={config.icon}
        size="small"
      />
    );
  };

  if (loading) {
    return (
      <Box sx={{ pt: "64px", display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const currentPlan = getCurrentPlan();
  const currentPlanInfo = plans.find(plan => plan.id === currentPlan);

  return (
    <Box sx={{ pt: "64px" }}>
      {/* Header */}
      <Paper
        sx={{
          p: 1.5,
          pr: 3,
          backgroundColor: "#6A5AF9",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderRadius: "0px !important",
        }}
      >
        <Typography
          variant="overline"
          sx={{ color: "white", ml: 4, fontSize: "1rem" }}
        >
          {t('subscription.title').toUpperCase()}
        </Typography>
        <Typography variant="body1" sx={{ color: "white", pr: 1 }}>
          {organizationName}
        </Typography>
        <Box>
          {/* Espacio para logo si se necesita */}
        </Box>
      </Paper>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Plan Actual */}
        <Box sx={{ mb: 6 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {t('subscription.currentPlan')}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={loadSubscriptionData}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
            >
              {loading ? t('subscription.actions.updating') : t('subscription.actions.update')}
            </Button>
          </Box>

          {subscriptionData ? (
            <Card sx={{ maxWidth: 600, mt: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PremiumIcon sx={{ mr: 1, color: '#1976d2' }} />
                    <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                      {currentPlanInfo?.name}
                    </Typography>
                  </Box>
                  {getStatusChip(subscriptionData.status)}
                </Box>

                <Typography variant="h6" sx={{ color: '#1976d2', mb: 2 }}>
                  {currentPlan === "starter" ? (
                    t('subscription.free')
                  ) : (
                    `${PRICING[subscriptionData.plan][subscriptionData.subscription_type]} USD/${subscriptionData.subscription_type || t('subscription.monthly')}`
                  )}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      {t('subscription.fields.startDate')}
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(subscriptionData.start_date)}
                    </Typography>
                  </Grid>

                  {currentPlan !== "starter" && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        {t('subscription.fields.nextBilling')}
                      </Typography>
                      <Typography variant="body1">
                        {subscriptionData.next_billing_date ?
                          formatDate(subscriptionData.next_billing_date) :
                          t('subscription.fields.notApplicable')
                        }
                      </Typography>
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      {t('subscription.fields.limitsReset')}
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(subscriptionData.usage_reset_date)}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      {t('subscription.fields.currentPeriodEnd')}
                    </Typography>
                    <Typography variant="body1">
                      {formatDate(subscriptionData.current_period_end)}
                    </Typography>
                  </Grid>
                </Grid>

                {subscriptionData.status === 'suspended' && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {t('subscription.alerts.suspended', { endDate: formatDate(subscriptionData.current_period_end) })}
                  </Alert>
                )}

                {/* Alerta si hay discrepancia entre suscripción y pagos */}
                {(() => {
                  const recentPayment = paymentHistory
                    .filter(p => p.status === 'completed')
                    .sort((a, b) => {
                      const timeA = a.created_at._seconds || a.created_at.seconds || 0;
                      const timeB = b.created_at._seconds || b.created_at.seconds || 0;
                      return timeB - timeA;
                    })[0];

                  if (recentPayment &&
                    subscriptionData &&
                    recentPayment.plan !== subscriptionData.plan &&
                    recentPayment.amount > 0) {
                    return (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        {t('subscription.alerts.recentPayment', { plan: recentPayment.plan })}
                      </Alert>
                    );
                  }
                  return null;
                })()}

                {/* Acciones */}
                {subscriptionData.paypal_subscription_id && (
                  <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    {subscriptionData.status === 'active' && (
                      <Button
                        variant="outlined"
                        color="warning"
                        startIcon={<WarningIcon />}
                        onClick={() => setConfirmModal({
                          open: true,
                          type: 'suspend',
                          data: subscriptionData
                        })}
                        disabled={actionLoading}
                      >
                        {t('subscription.actions.suspendButton')}
                      </Button>
                    )}

                    {subscriptionData.status === 'suspended' && (
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<PlayArrowIcon />}
                        onClick={handleReactivate}
                        disabled={actionLoading}
                      >
                        {actionLoading ? t('subscription.actions.processing') : t('subscription.actions.reactivateButton')}
                      </Button>
                    )}
                  </Box>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card sx={{ maxWidth: 600, mt: 3 }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PremiumIcon sx={{ mr: 1, color: '#4caf50' }} />
                  <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    {t('subscription.freePlan.title')}
                  </Typography>
                  <Chip label={t('subscription.freePlan.current')} size="small" sx={{ ml: 2 }} />
                </Box>
                <Typography variant="body1" color="text.secondary">
                  {t('subscription.freePlan.description')}
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>

        {/* Planes Disponibles */}
        <Box sx={{ mb: 6 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {t('subscription.availablePlans')}
            </Typography>

            {/* Switch para cambiar entre mensual y anual */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" sx={{ color: !isYearlyView ? '#1976d2' : 'text.secondary', fontWeight: !isYearlyView ? 'bold' : 'normal' }}>
                {t('subscription.billingCycleMonthly')}
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={isYearlyView}
                    onChange={(e) => setIsYearlyView(e.target.checked)}
                    color="primary"
                  />
                }
                label=""
                sx={{ m: 0 }}
              />
              <Typography variant="body2" sx={{ color: isYearlyView ? '#1976d2' : 'text.secondary', fontWeight: isYearlyView ? 'bold' : 'normal' }}>
                {t('subscription.billingCycleYearly')}
              </Typography>
              {isYearlyView && (
                <Chip
                  label={t('subscription.savingsUpTo8')}
                  size="small"
                  color="success"
                  sx={{ ml: 1 }}
                />
              )}
            </Box>
          </Box>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            {subscriptionData?.status === 'suspended'
              ? t('subscription.availablePlansSuspendedDescription')
              : t('subscription.availablePlansDescription')
            }
          </Typography>

          <Grid container spacing={3}>
            {plans.map((plan) => {
              const isCurrentPlan = plan.id === currentPlan;
              const canUpgrade = canUpgradeToplan(plan.id);
              const isDowngrade = PLAN_RANK[plan.id] < PLAN_RANK[currentPlan];

              return (
                <Grid item xs={12} sm={6} md={3} key={plan.id}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      position: 'relative',
                      border: isCurrentPlan ? '2px solid #1976d2' : '1px solid #e0e0e0',
                      transform: plan.recommended ? 'scale(1.05)' : 'scale(1)',
                      '&:hover': {
                        transform: plan.recommended ? 'scale(1.07)' : 'scale(1.02)',
                        boxShadow: 3,
                      },
                      transition: 'all 0.3s ease',
                      minHeight: 450,
                    }}
                  >
                    {plan.recommended && (
                      <Chip
                        label={t('subscription.recommended')}
                        color="primary"
                        icon={<StarIcon />}
                        sx={{
                          position: 'absolute',
                          top: 5,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          zIndex: 1,
                        }}
                      />
                    )}

                    <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 2, mb: 1, textAlign: 'center' }}>
                        {plan.name}
                      </Typography>

                      <Box sx={{ textAlign: 'center', mb: 2 }}>
                        {plan.price === 0 ? (
                          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                            {t('subscription.free')}
                          </Typography>
                        ) : plan.customPricing ? (
                          <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                            {t('subscription.customPricing')}
                          </Typography>
                        ) : (
                          <Box>
                            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                              ${plan.price}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              /{plan.period}
                            </Typography>
                            {isYearlyView && plan.price > 0 && (
                              <Typography variant="caption" color="success.main" display="block">
                                {t('subscription.equivalencePerMonth', { amount: Math.round(plan.price / 12) })}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </Box>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center', minHeight: 40 }}>
                        {plan.description}
                      </Typography>

                      <Box sx={{ mt: 'auto' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                          {t('subscription.features')}:
                        </Typography>
                        {plan.features.map((feature, index) => (
                          <Typography key={index} variant="body2" sx={{
                            display: 'flex',
                            alignItems: 'center',
                            mb: 0.5,
                            fontSize: '0.85rem'
                          }}>
                            <CheckIcon sx={{ fontSize: 16, color: '#2e7d32', mr: 1 }} />
                            {feature}
                          </Typography>
                        ))}
                      </Box>

                      {/* Botones de acción */}
                      <Box sx={{ mt: 2, textAlign: 'center' }}>
                        {isCurrentPlan ? (
                          <Button
                            variant="outlined"
                            disabled
                            fullWidth
                            sx={{
                              borderColor: '#1976d2',
                              color: '#1976d2',
                              fontWeight: 'bold'
                            }}
                          >
                            {t('subscription.currentPlanBadge')}
                          </Button>
                        ) : plan.customPricing ? (
                          <Typography variant="body2" color="text.secondary">
                            {t('subscription.contactEmail')}: <a href="mailto:david@amandahr.ai" style={{ color: '#1976d2' }}>david@amandahr.ai</a>
                          </Typography>
                        ) : canUpgrade ? (
                          <Button
                            variant="contained"
                            fullWidth
                            onClick={() => handleSubscribe(plan.id)}
                            disabled={actionLoading}
                            sx={{
                              fontWeight: 'bold',
                              bgcolor: plan.recommended ? '#ff9800' : '#1976d2',
                              '&:hover': {
                                bgcolor: plan.recommended ? '#f57c00' : '#1565c0',
                              }
                            }}
                          >
                            {actionLoading ? t('subscription.actions.processing') : (plan.price === 0 ? t('subscription.actions.select') : t('subscription.upgrade'))}
                          </Button>
                        ) : isDowngrade ? (
                          <Tooltip title={t('subscription.tooltips.downgrade')}>
                            <span>
                              <Button
                                variant="outlined"
                                fullWidth
                                disabled
                                sx={{
                                  fontWeight: 'bold',
                                  opacity: 0.5
                                }}
                              >
                                {t('subscription.actions.downgradeNotAvailable')}
                              </Button>
                            </span>
                          </Tooltip>
                        ) : (
                          <Button
                            variant="outlined"
                            disabled
                            fullWidth
                            sx={{ opacity: 0.5 }}
                          >
                            {t('subscription.actions.notAvailable')}
                          </Button>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>

        {/* Historial de Pagos */}
        {paymentHistory.length > 0 && (
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 3 }}>
              <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              {t('subscription.paymentHistory.title')}
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              {t('subscription.paymentHistory.description')}
            </Alert>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('subscription.paymentHistory.headers.date')}</TableCell>
                    <TableCell>{t('subscription.paymentHistory.headers.concept')}</TableCell>
                    <TableCell>{t('subscription.paymentHistory.headers.amount')}</TableCell>
                    <TableCell>{t('subscription.paymentHistory.headers.status')}</TableCell>
                    <TableCell>{t('subscription.paymentHistory.headers.method')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentHistory.slice(0, 10).map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell>
                        {formatDate(payment.created_at || payment.created)}
                      </TableCell>
                      <TableCell>
                        {payment.plan ? t('subscription.paymentHistory.planConcept', { plan: payment.plan }) : payment.type || t('subscription.paymentHistory.payment')}
                      </TableCell>
                      <TableCell>
                        ${payment.amount} {payment.currency}
                        {payment.amount !== PRICING[payment.plan]?.monthly && payment.plan !== 'starter' && (
                          <Typography variant="caption" color="error" display="block">
                            ({t('subscription.paymentHistory.expected')}: ${PRICING[payment.plan]?.monthly})
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {getStatusChip(payment.status)}
                      </TableCell>
                      <TableCell>
                        PayPal
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Container>

      {/* Modal de Confirmación */}
      <Dialog
        open={confirmModal.open}
        onClose={() => setConfirmModal({ open: false, type: '', data: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {confirmModal.type === 'suspend' ? t('subscription.modals.suspendTitle') : t('subscription.modals.confirmTitle')}
        </DialogTitle>
        <DialogContent>
          {confirmModal.type === 'suspend' && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  {t('subscription.modals.suspendDescription')}:
                </Typography>
                <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                  <li>{t('subscription.modals.suspendPointAccess', { endDate: formatDate(confirmModal.data?.current_period_end) })}</li>
                  <li>{t('subscription.modals.suspendPointNoBilling')}</li>
                  <li>{t('subscription.modals.suspendPointReactivate', { endDate: formatDate(confirmModal.data?.current_period_end) })}</li>
                  <li>{t('subscription.modals.suspendPointCancellation')}</li>
                </ul>
              </Alert>
              <Typography variant="body1">
                {t('subscription.modals.suspendConfirmation')}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmModal({ open: false, type: '', data: null })}
            disabled={actionLoading}
          >
            {t('subscription.actions.cancel')}
          </Button>
          <Button
            onClick={handleSuspend}
            color="warning"
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? t('subscription.actions.processing') : t('subscription.actions.suspendButton')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Upgrade */}
      <Dialog
        open={upgradeModal.open}
        onClose={() => setUpgradeModal({ open: false, targetPlan: null, currentPlan: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('subscription.modals.upgradeTitle')}
        </DialogTitle>
        <DialogContent>
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                {t('subscription.modals.upgradeDescription', {
                  currentPlan: plans.find(p => p.id === upgradeModal.currentPlan)?.name,
                  targetPlan: plans.find(p => p.id === upgradeModal.targetPlan)?.name
                })}
              </Typography>
            </Alert>

            <Typography variant="body1" sx={{ mb: 2 }}>
              {t('subscription.modals.upgradeConfirm')}:
            </Typography>

            <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {t('subscription.modals.upgradePointCancel')}
                </Typography>
              </li>
              <li>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {t('subscription.modals.upgradePointCharge', { amount: PRICING[upgradeModal.targetPlan]?.monthly })}
                </Typography>
              </li>
              <li>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {t('subscription.modals.upgradePointLimitsReset')}
                </Typography>
              </li>
              <li>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {t('subscription.modals.upgradePointBillingDate')}
                </Typography>
              </li>
            </ul>

            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                {t('subscription.modals.upgradeWarning')}
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setUpgradeModal({ open: false, targetPlan: null, currentPlan: null })}
            disabled={actionLoading}
          >
            {t('subscription.actions.cancel')}
          </Button>
          <Button
            onClick={handleUpgradeConfirmed}
            color="primary"
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? t('subscription.actions.processing') : t('subscription.actions.confirmUpgrade')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Términos y Condiciones */}
      <Dialog
        open={termsModal.open}
        onClose={() => setTermsModal({ open: false, targetPlan: null, subscriptionType: 'monthly', accepted: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {t('subscription.terms.title')}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t('subscription.terms.description')}
          </Typography>

          <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              <strong>{t('subscription.terms.planSelected')}:</strong> {plans.find(p => p.id === termsModal.targetPlan)?.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>{t('subscription.terms.billingCycle')}:</strong> {termsModal.subscriptionType === 'yearly' ? t('subscription.yearly') : t('subscription.monthly')}
            </Typography>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={termsModal.accepted}
                onChange={(e) => setTermsModal(prev => ({ ...prev, accepted: e.target.checked }))}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                {t('subscription.terms.agreement')}{' '}
                <Button
                  variant="text"
                  size="small"
                  href="https://storage.googleapis.com/amandahr/terminos_condiciones_amanda.docx"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
                >
                  {t('subscription.terms.termsAndConditions')}
                </Button>
              </Typography>
            }
            sx={{
              alignItems: 'center',
              '& .MuiFormControlLabel-label': {
                lineHeight: 1.2
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setTermsModal({ open: false, targetPlan: null, subscriptionType: 'monthly', accepted: false })}
            color="inherit"
          >
            {t('subscription.actions.cancel')}
          </Button>
          <Button
            onClick={handleTermsAccepted}
            variant="contained"
            disabled={!termsModal.accepted || actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : null}
          >
            {actionLoading ? t('subscription.actions.processing') : t('subscription.terms.continue')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubscriptionPlansLayout;